import store from "../store";
import {
  getActiveEndpoint,
  getPreferredEndpoint,
  markEndpointFailed,
} from "./endpointManager";

// Cache del endpoint activo para evitar health checks repetidos en requests cercanos
let cachedEndpoint: string | null = null;
let cacheTimestamp = 0;
const CACHE_DURATION = 30000; // 30 segundos

/**
 * Obtiene el endpoint activo, usando cache si está disponible y fresco.
 */
async function getEndpoint(): Promise<string> {
  const now = Date.now();

  // Si el cache es reciente, usarlo
  if (cachedEndpoint && now - cacheTimestamp < CACHE_DURATION) {
    return cachedEndpoint;
  }

  // Obtener endpoint activo con health check
  cachedEndpoint = await getActiveEndpoint();
  cacheTimestamp = now;
  return cachedEndpoint;
}

/**
 * Invalida el cache del endpoint (llamar cuando un request falla)
 */
function invalidateCache(): void {
  cachedEndpoint = null;
  cacheTimestamp = 0;
}

/**
 * Realiza un request a un endpoint específico.
 */
async function doRequest(
  baseUrl: string,
  resource: string,
  queryParam: string | undefined,
  body: object,
  contentType: string,
  jwt: string
): Promise<Response> {
  const url = `${baseUrl}${resource}.php${queryParam ? `?${queryParam}` : ""}`;
  const headers: HeadersInit = {
    "Content-Type": contentType,
    Authorization: `Bearer ${jwt}`,
  };

  return fetch(url, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
    cache: "no-cache",
  });
}

/**
 * Procesa la respuesta según el content type.
 */
async function processResponse(
  res: Response,
  contentType: string,
  jwt: string | undefined
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<any> {
  if (contentType === "application/json") {
    const response = await res.json();
    if (response?.mensaje?.codigo === "999" && !jwt) {
      localStorage.clear();
      window.location.href = "";
    }
    return response;
  }
  return res.text();
}

export default function Api(
  resource: string,
  jwt?: string,
  queryParam?: string
) {
  const authJwt = jwt || store.getState().auth.jwt;

  return {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    post: async (body = {}, contentType = "application/json"): Promise<any> => {
      // Obtener endpoint activo
      let endpoint = await getEndpoint();

      try {
        const res = await doRequest(
          endpoint,
          resource,
          queryParam,
          body,
          contentType,
          authJwt
        );

        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }

        return processResponse(res, contentType, jwt);
      } catch {
        // Primer intento falló, marcar y obtener siguiente endpoint
        invalidateCache();
        const nextEndpoint = markEndpointFailed(endpoint);

        // Segundo intento con endpoint alternativo
        try {
          const res = await doRequest(
            nextEndpoint,
            resource,
            queryParam,
            body,
            contentType,
            authJwt
          );

          if (!res.ok) {
            throw new Error(`HTTP ${res.status}`);
          }

          // Actualizar cache con el endpoint que funcionó
          cachedEndpoint = nextEndpoint;
          cacheTimestamp = Date.now();

          return processResponse(res, contentType, jwt);
        } catch {
          // Tercer intento: usar el endpoint preferido sincrónicamente
          const fallbackEndpoint = getPreferredEndpoint();

          try {
            const res = await doRequest(
              fallbackEndpoint,
              resource,
              queryParam,
              body,
              contentType,
              authJwt
            );

            return processResponse(res, contentType, jwt);
          } catch {
            throw new Error(
              "Hubo un problema con la conexión al servidor, por favor intentelo mas tarde."
            );
          }
        }
      }
    },
  };
}
